// 「直感に従う」の入口と提案文。日盤の中宮星の五行から提案を選ぶ。
export type EntryType = 'travel' | 'create' | 'rest' | 'move' | 'meet' | 'learn';
export type Elem = '水' | '木' | '火' | '土' | '金';

export const ENTRIES: { id: EntryType; icon: string; label: string }[] = [
  { id: 'travel', icon: '🚶', label: 'どこかへ行きたい' },
  { id: 'create', icon: '🎨', label: '創る' },
  { id: 'rest',   icon: '🌙', label: '休む' },
  { id: 'move',   icon: '🏃', label: '動く' },
  { id: 'meet',   icon: '🤝', label: '人と会う' },
  { id: 'learn',  icon: '📖', label: '学ぶ' },
];

// 五行の相生（この気が生む次の気）
const GEN: Record<Elem, Elem> = { 水: '木', 木: '火', 火: '土', 土: '金', 金: '水' };

const SUGGESTIONS: Record<EntryType, Record<Elem, string[]>> = {
  travel: {
    水: ['水辺をのんびり歩く', '静かな喫茶店でひと息つく', '温泉や銭湯で温まる'],
    木: ['緑の多い公園を歩く', '朝の空気を吸いに少し遠回りする', '気になっていたお店をのぞく'],
    火: ['景色のひらけた高い場所へ行く', '美術館やギャラリーへ行く', '明るい街なかを散歩する'],
    土: ['昔からある神社やお寺へ行く', '地元の直売所や市場へ行く', '馴染みのある場所へ行く'],
    金: ['美味しいものを食べに出かける', '夕方の空を見に行く', '好きなお店でゆっくり選ぶ'],
  },
  create: {
    水: ['思っていることを書き出してみる', '静かな音楽をかけて手を動かす', '写真を撮りながら歩く'],
    木: ['新しいことを一つ試しに作る', '声に出して歌ってみる', '植物を植える・育てる'],
    火: ['色を使って描いてみる', '身の回りを一つ飾ってみる', '作ったものを誰かに見せる'],
    土: ['手間をかけた料理をつくる', '途中のものを少し進める', '土や粘土に手でふれる'],
    金: ['お菓子や飲み物をつくる', '道具を手入れする', '好きなものを集めて並べる'],
  },
  rest: {
    水: ['早めにお風呂に入って眠る', '何もしない時間を30分つくる', '温かい飲み物でぼーっとする'],
    木: ['窓を開けて深呼吸する', '軽く体を伸ばして横になる', 'スマホを置いて目を閉じる'],
    火: ['明かりを落として静かに過ごす', '好きな香りでくつろぐ', '早めに布団に入る'],
    土: ['家でごろごろする日にする', '温かいご飯をゆっくり食べる', '部屋の一角だけ整えて座る'],
    金: ['好きなおやつでひと休み', '好きな音楽で気持ちをゆるめる', '少しだけ昼寝をする'],
  },
  move: {
    水: ['ゆっくり泳ぐ・水の中を歩く', '夜にのんびり散歩する', '呼吸に合わせて体を伸ばす'],
    木: ['朝、少し速めに歩く', '自転車で近所をまわる', '体を大きく伸ばすヨガ'],
    火: ['汗をかくくらい体を動かす', '音楽に合わせて踊る', '日の当たる場所で体を動かす'],
    土: ['草むしりや庭仕事をする', '階段や坂道を歩く', '掃除で体を動かす'],
    金: ['軽く走ってみる', '姿勢を整える体操をする', '誰かと一緒に歩く'],
  },
  meet: {
    水: ['久しぶりの人に連絡してみる', '少人数でゆっくり話す', '話を聞いてくれる人に会う'],
    木: ['新しい集まりに顔を出す', '気になっていた人に声をかける', '電話で声を聞く'],
    火: ['会いたい人に会いに行く', '人前で話す・見せる', '褒めたい人に伝える'],
    土: ['家族とゆっくり過ごす', '身近な人とご飯を食べる', 'いつもの仲間に会う'],
    金: ['美味しいものを囲んで話す', '笑い合える人と過ごす', 'お礼を伝えに行く'],
  },
  learn: {
    水: ['一冊の本をじっくり読む', '気になることを調べて書きとめる', '静かな場所で考えごとをする'],
    木: ['新しい分野の入門をのぞく', '講座や動画で一つ学ぶ', '言葉や語学にふれる'],
    火: ['美しいものを見て感性を磨く', '学んだことを人に話す', '直感で選んだ本を開く'],
    土: ['前に学んだことを見直す', '続けていることを一歩進める', '暮らしの知恵を調べる'],
    金: ['暮らしやお金の整え方を学ぶ', '好きな分野をもっと深く知る', '詳しい人に教わる'],
  },
};

/** 今日の提案の並び（最大6件）。今日の気の3件を日替わりの順で先に、続けて相生の気の3件。 */
export function getSuggestions(entry: EntryType, elem: Elem, date: Date): string[] {
  const dayIndex = Math.floor(date.getTime() / 86400000);
  const own = SUGGESTIONS[entry][elem];
  const off = dayIndex % own.length;
  const rotated = [...own.slice(off), ...own.slice(0, off)];
  return [...rotated, ...SUGGESTIONS[entry][GEN[elem]]];
}

const ELEM_REASON: Record<Elem, string> = {
  水: '流れる・静まる・つながる気が強い日です。ゆるやかで静かな動きがなじみやすいと見ています。',
  木: '伸びる・始まる気が強い日です。新しいことや、外の空気にふれる動きがなじみやすいと見ています。',
  火: '明るく表に出る気が強い日です。見る・見せる・表すことがなじみやすいと見ています。',
  土: '落ち着く・積み重ねる気が強い日です。身近なことや、続けてきたことがなじみやすいと見ています。',
  金: '実る・楽しむ気が強い日です。味わうことや、整えることがなじみやすいと見ています。',
};

// getStarRel(本命星, 日盤中宮星) の結果ごとの一言
const REL_REASON: Record<string, string> = {
  比和: 'あなたの本命星とは同じ気どうし。今日の気になじみやすい関係です。',
  相生受: 'あなたの本命星は、今日の気から力をもらいやすい関係です。',
  相生生: 'あなたの本命星が今日の気に力を注ぐ関係です。少し控えめでもよさそうです。',
  相剋受: 'あなたの本命星は今日の気とぶつかりやすい関係です。いつもより一歩ゆっくりが合いそうです。',
  相剋攻: 'あなたの本命星が今日の気を押す関係です。張り切りすぎない程度がよさそうです。',
};

/** 見立ての理由（有料）。相生の気からの提案かどうかで一文を足す。 */
export function getReason(opts: {
  dayStarName: string; elem: Elem; index: number;
  honmeiName?: string | null; honmeiRel?: string | null;
}): string[] {
  const { dayStarName, elem, index, honmeiName, honmeiRel } = opts;
  const lines = [`今日の日盤の中心は${dayStarName}（${elem}の気）。${ELEM_REASON[elem]}`];
  if (index >= 3) lines.push(`この提案は、${elem}の気が生み出す「${GEN[elem]}」の気から選んでいます。`);
  if (honmeiName && honmeiRel && REL_REASON[honmeiRel]) lines.push(`${honmeiName}：${REL_REASON[honmeiRel]}`);
  lines.push('どれも参考のひとつです。最後はあなたの感覚を大事にしてください。');
  return lines;
}

// 身体の反応。答えは命令ではなくヒントとして返す。
export type BodyReaction = 'light' | 'heavy' | 'forward' | 'restless';

export const BODY_OPTIONS: { id: BodyReaction; icon: string; label: string }[] = [
  { id: 'light',    icon: '🪶', label: '軽い' },
  { id: 'heavy',    icon: '🪨', label: '重い' },
  { id: 'forward',  icon: '➡️', label: '前のめり' },
  { id: 'restless', icon: '〰️', label: 'ざわざわ' },
];

/** 頭（ワクワクか）と身体の答えが食い違うときの一言。食い違わなければ null。 */
export function getBodyGap(excited: boolean, body: BodyReaction): string | null {
  if (excited && body === 'heavy') {
    return '頭はワクワク、身体は少し重め。疲れているだけかもしれないし、どこかに引っかかりがあるのかもしれません。行ってもいいし、少し休んでから決めてもいい。決めるのはあなたです。';
  }
  if (!excited && (body === 'light' || body === 'forward')) {
    return `頭は「ちょっと違う」、でも身体は${body === 'light' ? '軽やか' : '前のめり'}。もしかすると、この提案のどこかに惹かれているのかもしれません。`;
  }
  return null;
}

export const DONE_MESSAGE: Record<EntryType, string> = {
  travel: 'いいですね。気持ちのままに、いってらっしゃい。',
  create: 'いいですね。楽しんで手を動かしてね。',
  rest:   'いいですね。今日はゆっくりしてね。',
  move:   'いいですね。気持ちよく体を動かしてね。',
  meet:   'いいですね。いい時間になりますように。',
  learn:  'いいですね。わくわくを大事に学んでね。',
};
